<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import Button from './Button.vue';

const props = defineProps({
  testimonials: {
    type: Array,
    required: true
  }
});

// State for the carousel
const testimonialsListRef = ref(null);
const testimonialContainerHeight = ref('auto');
const currentInterviewIndex = ref(0);

// Watch for changes to recalculate heights
watch(() => props.testimonials, () => {
  nextTick(calculateTestimonialHeight);
}, { immediate: true });

function calculateTestimonialHeight() {
  // Only calculate if we have testimonials
  if (!props.testimonials || props.testimonials.length === 0) {
    testimonialContainerHeight.value = '200px';
    return;
  }

  // Create a more accurate hidden div that matches our testimonial styling exactly
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.visibility = 'hidden';
  tempDiv.style.width = '100%';
  tempDiv.style.left = '-9999px';
  tempDiv.style.zIndex = '-1000';
  
  // Match the exact styling of the testimonial text
  tempDiv.style.fontSize = window.innerWidth >= 768 ? '1.875rem' : '1.25rem';
  tempDiv.style.fontWeight = '500';
  tempDiv.style.lineHeight = '1.625';
  tempDiv.style.padding = '0 1rem';
  
  document.body.appendChild(tempDiv);

  // Find max height among ALL testimonials (not just current one)
  let maxHeight = 0;
  
  props.testimonials.forEach((product, index) => {
    if (!product?.testimonial?.answer3) return;
    
    // Simple text measurement without complex HTML
    tempDiv.textContent = product.testimonial.answer3;
    const height = tempDiv.scrollHeight;
    maxHeight = Math.max(maxHeight, height);
  });
  
  // Clean up
  document.body.removeChild(tempDiv);
  
  // Set height with padding
  testimonialContainerHeight.value = `${Math.max(maxHeight + 100, 200)}px`;
}

// Call the calculation on mount and when testimonials change
onMounted(() => {
  // Wait for the DOM to be fully rendered
  nextTick(() => {
    // Calculate initial height
    calculateTestimonialHeight();
    
    // Re-calculate on window resize
    window.addEventListener('resize', calculateTestimonialHeight);
    
    // Force a recalculation after a short delay to handle any rendering delays
    setTimeout(calculateTestimonialHeight, 500);
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', calculateTestimonialHeight);
});

// Navigation methods for the interview carousel
function nextInterview() {
  if (currentInterviewIndex.value < props.testimonials.length - 1) {
    currentInterviewIndex.value++;
  } else {
    currentInterviewIndex.value = 0; // Loop to the beginning
  }
}

function prevInterview() {
  if (currentInterviewIndex.value > 0) {
    currentInterviewIndex.value--;
  } else {
    currentInterviewIndex.value = props.testimonials.length - 1; // Loop to the end
  }
}
</script>

<template>
  <div class="w-full">    
    <div ref="testimonialsListRef">
      <!-- Fixed testimonial structure with dynamic content - Responsive dark theme -->
      <div class="flex flex-col md:flex-row bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-black/30 overflow-hidden border border-gray-200 dark:border-gray-700">
        <!-- Left sidebar with blurred background and logo -->
        <div class="h-auto min-h-32 md:w-1/4 lg:w-1/5 relative overflow-hidden">
          <!-- Background image with subtle gradient overlay -->
          <div class="absolute inset-0 w-full h-full">
            <!-- Restored fade transition -->
            <transition name="fade" mode="out-in">
              <img :key="`bg-${currentInterviewIndex}`" :src="testimonials[currentInterviewIndex]?.testimonial?.image" :alt="testimonials[currentInterviewIndex]?.testimonial?.author + testimonials[currentInterviewIndex]?.meta?.name" class="w-full h-full object-cover object-center blur-[1px] brightness-[0.85] dark:brightness-[0.7] scale-105"/>
            </transition>
            <!-- Responsive gradient overlay -->
            <div class="absolute inset-0 bg-gradient-to-b from-black/5 via-black/15 to-black/30 dark:from-gray-900/60 dark:via-gray-900/70 dark:to-gray-900/80"></div>
          </div>
          
          <!-- Content container with logo at bottom -->
          <div class="z-10 p-6 md:py-8 h-full flex flex-col items-center justify-between relative">                        
            <!-- Company logo/icon with responsive theme background -->
            <div class="w-full flex items-center justify-center mt-auto">
              <transition name="fade" mode="out-in">
                <img :key="`logo-${currentInterviewIndex}`" :src="testimonials[currentInterviewIndex]?.meta?.logo" :alt="testimonials[currentInterviewIndex]?.meta?.name + ' logo'" class="h-16 md:h-20 w-auto object-contain"/>
              </transition>
            </div>
          </div>
        </div>
        
        <!-- Main testimonial content with responsive dark theme -->
        <div class="md:w-3/4 lg:w-4/5 p-6 md:p-8 flex flex-col">
          <!-- Project title with responsive text colors -->
          <h3 class="text-lg md:text-2xl font-medium text-gray-900 dark:text-white leading-tight mb-6">
            <transition name="fade" mode="out-in">
              <span :key="`title-${currentInterviewIndex}`">
                {{ testimonials[currentInterviewIndex]?.testimonial?.question3 }}
              </span>
            </transition>
          </h3>
          
          <!-- Answer with fixed minimum height container and responsive quote marks -->
          <div class="mb-8 flex-grow pl-2 md:pl-4" :style="{ minHeight: testimonialContainerHeight }">
            <transition name="fade" mode="out-in">
              <p :key="`text-${currentInterviewIndex}`" class="testimonial-text z-10 text-xl md:text-3xl font-medium leading-relaxed text-gray-800 dark:text-gray-200">
                <span class="-ml-7 mr-7 md:-ml-10 md:mr-10 relative">
                  <span class="inline-block text-6xl md:text-8xl text-orange-100 dark:text-orange-400/60 align-middle leading-none absolute -top-4 md:-top-6">"</span>
                </span>
                {{ testimonials[currentInterviewIndex]?.testimonial?.answer3 }}
                <span class="relative">
                  <span class="inline-block text-6xl md:text-8xl text-orange-100 dark:text-orange-400/60 align-middle leading-none absolute -top-4 md:-top-6">"</span>
                </span>
              </p>
            </transition>
          </div>
          
          <!-- Author info and CTA with responsive dark theme colors -->
          <div class="flex flex-col md:flex-row gap-3 sm:gap-4 w-full md:w-auto justify-between items-end">
            <p> 
              <transition name="fade" mode="out-in">
                <span :key="`author-${currentInterviewIndex}`">
                  <span class="font-semibold text-lg text-gray-900 dark:text-white">{{ testimonials[currentInterviewIndex]?.testimonial?.author }},</span>
                  <br>
                  <span class="font-medium text-base text-jet/70 dark:text-gray-400">{{ testimonials[currentInterviewIndex]?.testimonial?.role }} —</span>
                  <span class="font-medium text-base text-orange-500/70 dark:text-orange-400/80 text-shadow-2xs ml-1"> {{ testimonials[currentInterviewIndex]?.meta?.name }}</span>
                </span>
              </transition>
            </p>
            <Button :href="'/project/' + testimonials[currentInterviewIndex]?.meta?.slug" variant="gradient" icon="arrow-right" iconPosition="right" size="large" :block="true">
              Bekijk dit interview
            </Button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Navigation and links bar with responsive dark theme -->
    <div class="flex flex-row justify-between items-center gap-4 mt-6" v-if="testimonials && testimonials.length > 1">
      <!-- Left - Button group with responsive dark theme indicators -->
      <div class="flex gap-2">
        <span v-for="i in testimonials.length" :key="i" :class="`block h-1 md:h-2 w-4 md:w-8 rounded-xs transition-all duration-300 cursor-pointer ${i-1 === currentInterviewIndex ? 'bg-orange-200 dark:bg-orange-400 w-4 md:w-8' : 'bg-gray-100 dark:bg-gray-600 hover:bg-orange-100 dark:hover:bg-gray-500'}`" @click="currentInterviewIndex = i-1"></span>
      </div>
      
      <!-- Right - Navigation controls with responsive dark theme -->
      <div class="flex justify-center md:justify-end gap-3 mt-4 md:mt-0">
        <button @click="prevInterview" :disabled="testimonials.length <= 1" :class="`h-10 w-10 flex items-center justify-center rounded-full transition-all duration-300 ${testimonials.length <= 1 ? 'bg-gray-100/30 dark:bg-gray-700/30 text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'bg-black/60 dark:bg-gray-700/80 text-gray-200 dark:text-gray-300 hover:bg-black/80 dark:hover:bg-gray-600/80 dark:border dark:border-gray-600'}`" aria-label="Previous interview">
          <font-awesome-icon :icon="['fas', 'chevron-left']" class="h-4 w-4" />
        </button>
        <button @click="nextInterview" :disabled="testimonials.length <= 1" :class="`h-10 w-10 flex items-center justify-center rounded-full transition-all duration-300 ${testimonials.length <= 1 ? 'bg-gray-100/30 dark:bg-gray-700/30 text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'bg-black/60 dark:bg-gray-700/80 text-gray-200 dark:text-gray-300 hover:bg-black/80 dark:hover:bg-gray-600/80 dark:border dark:border-gray-600'}`" aria-label="Next interview">
          <font-awesome-icon :icon="['fas', 'chevron-right']" class="h-4 w-4" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Simplified transitions */
.transition-opacity {
  transition: opacity 0.3s ease;
}
</style>
